import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Stack,
    LinearProgress,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    Divider,
    Chip,
    Collapse,
    IconButton,
} from '@mui/material';
import {
    SyncAlt as SyncAltIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { importDuelsChunk } from '../../services/duels-sync-service';

const CHUNK_SIZE = 50;

const MODES = [
    {
        value: 'new',
        label: 'Tylko nowe',
        description: 'Mecze od ostatniego importu',
    },
    {
        value: 'since_date',
        label: 'Od daty',
        description: 'Mecze od wybranego dnia',
    },
    {
        value: 'all',
        label: 'Wszystkie',
        description: 'Cała historia z duels.ink',
    },
];

function DuelSyncImportWizard({ open, onClose, trackerFilters, lastSyncedAt }) {
    const [step, setStep] = useState('config'); // 'config' | 'running' | 'done'
    const [mode, setMode] = useState('new');
    const [fromDate, setFromDate] = useState('');
    const [showInfo, setShowInfo] = useState(false);
    const [progress, setProgress] = useState({ processed: 0, total: null, chunks: 0 });
    const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
    const [totals, setTotals] = useState({ imported: 0, pending: 0, potentialDuplicates: 0, skipped: 0 });
    const [error, setError] = useState(null);
    const cancelRef = useRef(false);

    const reset = () => {
        setStep('config');
        setMode('new');
        setFromDate('');
        setShowInfo(false);
        setProgress({ processed: 0, total: null, chunks: 0 });
        setTotals({ imported: 0, pending: 0, potentialDuplicates: 0, skipped: 0 });
        setError(null);
        setRateLimitCountdown(0);
        cancelRef.current = false;
    };

    const handleClose = () => {
        if (step === 'running') {
            cancelRef.current = true;
        }
        reset();
        onClose();
    };

    const handleStart = async () => {
        cancelRef.current = false;
        setStep('running');
        setError(null);
        setProgress({ processed: 0, total: null, chunks: 0 });
        setTotals({ imported: 0, pending: 0, potentialDuplicates: 0, skipped: 0 });

        let cursor = null;
        let totalImported = 0;
        let totalPending = 0;
        let totalDuplicates = 0;
        let totalSkipped = 0;

        // Tryb "Tylko nowe" — użyj lastSyncedAt jako fromDate, żeby backend zatrzymał się
        // gdy dotrze do gier starszych niż ostatni sync (zamiast paginować przez całą historię)
        const effectiveFromDate = mode === 'new' && lastSyncedAt ? lastSyncedAt : null;

        try {
            while (true) {
                if (cancelRef.current) break;

                const params = { chunkSize: CHUNK_SIZE, cursor };
                if (effectiveFromDate) {
                    params.fromDate = new Date(effectiveFromDate).toISOString();
                } else if (mode === 'since_date' && fromDate) {
                    params.fromDate = new Date(fromDate).toISOString();
                }
                if (trackerFilters?.format) params.format = trackerFilters.format;
                if (trackerFilters?.setId) params.setId = trackerFilters.setId;

                const result = await importDuelsChunk(params);

                // Obsługa rate limit (429) — poczekaj i ponów ten sam chunk
                if (result.rateLimited) {
                    const waitSecs = (result.retryAfterSeconds || 30) + 2;
                    for (let t = waitSecs; t > 0; t--) {
                        if (cancelRef.current) break;
                        setRateLimitCountdown(t);
                        await new Promise(r => setTimeout(r, 1000));
                    }
                    setRateLimitCountdown(0);
                    // cursor bez zmian — ponawiamy ten sam chunk
                    continue;
                }

                totalImported += result.imported || 0;
                totalPending += result.pending || 0;
                totalDuplicates += result.potentialDuplicates || 0;
                totalSkipped += result.skipped || 0;

                setProgress((prev) => ({
                    processed: prev.processed + (result.processed || 0),
                    total: prev.total,
                    chunks: prev.chunks + 1,
                }));
                setTotals({
                    imported: totalImported,
                    pending: totalPending,
                    potentialDuplicates: totalDuplicates,
                    skipped: totalSkipped,
                });

                if (result.done || !result.nextCursor) break;

                // Fallback early stop for 'new' mode (gdy brak lastSyncedAt)
                if (mode === 'new' && !effectiveFromDate && result.processed > 0 &&
                    result.imported === 0 && result.pending === 0 && result.potentialDuplicates === 0) {
                    break;
                }

                cursor = result.nextCursor;
            }
            setStep('done');
        } catch (err) {
            setError(err?.message || 'Wystąpił nieoczekiwany błąd podczas importu.');
            setStep('config');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={step !== 'running' ? handleClose : undefined}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1625',
                    border: '1px solid rgba(124, 58, 237, 0.3)',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <SyncAltIcon sx={{ color: '#a78bfa' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Import z duels.ink
                    </Typography>
                </Stack>
            </DialogTitle>

            <DialogContent>
                {/* ── CONFIG STEP ── */}
                {step === 'config' && (
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Wybierz zakres meczu do zaimportowania.
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setShowInfo((v) => !v)}
                                sx={{ color: showInfo ? '#a78bfa' : 'text.secondary', ml: 1 }}
                                title="Jak działa import?"
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Stack>

                        <Collapse in={showInfo}>
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(124, 58, 237, 0.08)',
                                    border: '1px solid rgba(124, 58, 237, 0.2)',
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#c4b5fd', display: 'block', mb: 1 }}>
                                    Jak działa import?
                                </Typography>
                                <Stack spacing={0.75}>
                                    {[
                                        { dot: '#4ade80', text: 'Zaimportowany automatycznie — talia pasuje do meczu (lub różni się tylko kilkoma kartami). Mecz od razu trafia do historii.' },
                                        { dot: '#fbbf24', text: 'Do weryfikacji — talia nie pasuje jednoznacznie. Trzeba przypisać ją ręcznie w zakładce duels.ink.' },
                                        { dot: '#60a5fa', text: 'Możliwy duplikat — mecz wygląda jak ręcznie wpisany. Możesz go scalić lub zignorować.' },
                                        { dot: '#9ca3af', text: 'Pominięty — mecz już istnieje w historii, nie jest importowany ponownie.' },
                                    ].map(({ dot, text }) => (
                                        <Stack key={dot} direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dot, mt: '4px', flexShrink: 0 }} />
                                            <Typography variant="caption" color="text.secondary">{text}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        </Collapse>

                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={(_, v) => v && setMode(v)}
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                        >
                            {MODES.map((m) => (
                                <ToggleButton
                                    key={m.value}
                                    value={m.value}
                                    sx={{
                                        textTransform: 'none',
                                        flex: 1,
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(124, 58, 237, 0.25)',
                                            color: '#c4b5fd',
                                            borderColor: 'rgba(124, 58, 237, 0.6)',
                                        },
                                    }}
                                >
                                    {m.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            {MODES.find((m) => m.value === mode)?.description}
                        </Typography>

                        {mode === 'since_date' && (
                            <TextField
                                fullWidth
                                type="date"
                                size="small"
                                label="Od dnia"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 2 }}
                            />
                        )}

                        {mode === 'all' && (
                            <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
                                Import całej historii może potrwać kilkadziesiąt sekund. Ręcznie wpisane mecze zostaną sprawdzone pod kątem duplikatów (±2h).
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ py: 0.5 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}

                {/* ── RUNNING STEP ── */}
                {step === 'running' && (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                        <SyncAltIcon
                            sx={{
                                fontSize: 40,
                                color: '#a78bfa',
                                animation: 'spin 1.5s linear infinite',
                                '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                                mb: 1,
                            }}
                        />
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            Importuję mecze...
                        </Typography>
                        {rateLimitCountdown > 0 ? (
                            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#fbbf24' }}>
                                Limit zapytań duels.ink — wznowienie za {rateLimitCountdown}s...
                            </Typography>
                        ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Przetworzono {progress.processed} gier ({progress.chunks} zapytań)
                            </Typography>
                        )}
                        <LinearProgress
                            variant="indeterminate"
                            sx={{
                                borderRadius: 1,
                                backgroundColor: 'rgba(124, 58, 237, 0.15)',
                                '& .MuiLinearProgress-bar': { backgroundColor: '#7c3aed' },
                            }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'center' }}>
                            {totals.imported > 0 && <Chip size="small" label={`${totals.imported} zaimportowanych`} sx={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#4ade80' }} />}
                            {totals.pending > 0 && <Chip size="small" label={`${totals.pending} do weryfikacji`} sx={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#fbbf24' }} />}
                            {totals.potentialDuplicates > 0 && <Chip size="small" label={`${totals.potentialDuplicates} duplikatów`} sx={{ backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa' }} />}
                        </Box>
                    </Box>
                )}

                {/* ── DONE STEP ── */}
                {step === 'done' && (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4ade80', mb: 1 }} />
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Import zakończony
                        </Typography>
                        <Stack spacing={1} sx={{ textAlign: 'left' }}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">Przetworzonych gier:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{progress.processed}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: '#4ade80' }}>Automatycznie zaimportowanych:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#4ade80' }}>{totals.imported}</Typography>
                            </Stack>
                            {totals.pending > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: '#fbbf24' }}>Do przypisania talii:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#fbbf24' }}>{totals.pending}</Typography>
                                </Stack>
                            )}
                            {totals.potentialDuplicates > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: '#60a5fa' }}>Możliwych duplikatów:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#60a5fa' }}>{totals.potentialDuplicates}</Typography>
                                </Stack>
                            )}
                            {totals.skipped > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Pominięto (już istnieją):</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{totals.skipped}</Typography>
                                </Stack>
                            )}
                        </Stack>

                        {(totals.pending > 0 || totals.potentialDuplicates > 0) && (
                            <Alert severity="info" sx={{ mt: 2, py: 0.5, textAlign: 'left' }}>
                                Przejdź do zakładki <strong>duels.ink</strong> w trackerze, żeby przypisać talie i rozwiązać duplikaty.
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {step === 'config' && (
                    <>
                        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
                            Anuluj
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleStart}
                            disabled={mode === 'since_date' && !fromDate}
                            startIcon={<SyncAltIcon />}
                            sx={{
                                backgroundColor: 'rgba(124, 58, 237, 0.8)',
                                '&:hover': { backgroundColor: '#7c3aed' },
                                textTransform: 'none',
                            }}
                        >
                            Rozpocznij import
                        </Button>
                    </>
                )}
                {step === 'running' && (
                    <Button
                        onClick={() => { cancelRef.current = true; }}
                        startIcon={<CloseIcon />}
                        sx={{ textTransform: 'none', color: '#f87171' }}
                    >
                        Zatrzymaj
                    </Button>
                )}
                {step === 'done' && (
                    <Button
                        variant="contained"
                        onClick={handleClose}
                        sx={{
                            backgroundColor: 'rgba(124, 58, 237, 0.8)',
                            '&:hover': { backgroundColor: '#7c3aed' },
                            textTransform: 'none',
                        }}
                    >
                        Gotowe
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default DuelSyncImportWizard;
